import styles from "./privacy-policy-page.module.scss";

const PrivacyPolicyPageComponent = () => {
  return (
    <main className={styles.policyContainer}>
      <article className={styles.content}>
        <header className={styles.header}>
          <div className={styles.badge}>Touche</div>
          <h1>Privacy Policy</h1>
          <p className={styles.updated}>Effective date: March 10, 2026</p>
        </header>

        <hr className={styles.divider} />

        <section>
          <h2>1. Introduction</h2>
          <p>
            This Privacy Policy describes how Touche (&quot;we&quot;,
            &quot;our&quot;, &quot;us&quot;) collects, uses, stores, and
            protects information when merchants (&quot;you&quot;,
            &quot;your&quot;) install and use the Touche application
            (&quot;App&quot;) through the Shopify platform. By installing or
            using the App, you acknowledge that you have read and understood
            this Privacy Policy.
          </p>
          <p>
            We are committed to protecting your privacy and handling your data
            in an open and transparent manner. This policy complies with the
            Shopify Partner Program Agreement, the Shopify API License and
            Terms of Use, and applicable data protection laws.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <p>
            When you install and use the App, we may collect the following
            categories of information:
          </p>

          <h3>2.1 Shopify Store Information</h3>
          <ul>
            <li>Store name, domain, and Shopify store identifier.</li>
            <li>
              Store owner contact details (name and email address) as provided
              by the Shopify API during the OAuth installation flow.
            </li>
            <li>
              Product catalog data, including product titles, descriptions,
              images, variants, and pricing.
            </li>
          </ul>

          <h3>2.2 Account and Authentication Data</h3>
          <ul>
            <li>
              Email address, name, and credentials used to register and log
              into the Touche admin panel.
            </li>
            <li>
              OAuth tokens and session data required to authenticate your
              connection with Shopify.
            </li>
          </ul>

          <h3>2.3 App Configuration Data</h3>
          <ul>
            <li>
              Settings and preferences you configure within the App, such as
              API keys, shipping options, and store configuration.
            </li>
            <li>
              Video content metadata and product-to-video associations created
              through the App.
            </li>
          </ul>

          <h3>2.4 Usage and Technical Data</h3>
          <ul>
            <li>
              Browser type, device information, IP address, and access
              timestamps.
            </li>
            <li>
              App interaction logs, page views, feature usage patterns, and
              error diagnostics.
            </li>
          </ul>

          <h3>2.5 Customer Data</h3>
          <p>
            The App may access end-customer data solely to the extent
            necessary for providing App functionality (for example, order
            information for analytics). We process this data on behalf of the
            merchant and in accordance with Shopify&apos;s API terms.
          </p>
        </section>

        <section>
          <h2>3. How We Use Information</h2>
          <p>We use the collected information for the following purposes:</p>
          <ul>
            <li>
              <strong>App Operation:</strong> To install, configure, and
              deliver core App features, including interactive video
              functionality and product synchronization.
            </li>
            <li>
              <strong>Authentication &amp; Security:</strong> To verify
              merchant identity, manage sessions, and protect against
              unauthorized access or misuse.
            </li>
            <li>
              <strong>Service Improvement:</strong> To analyze usage patterns,
              diagnose technical issues, and improve performance and user
              experience.
            </li>
            <li>
              <strong>Support:</strong> To respond to merchant inquiries,
              troubleshoot issues, and provide technical assistance.
            </li>
            <li>
              <strong>Communication:</strong> To send essential service
              notifications, updates, and security alerts related to the App.
            </li>
            <li>
              <strong>Legal Compliance:</strong> To comply with applicable
              laws, regulations, and Shopify platform requirements.
            </li>
          </ul>
        </section>

        <section>
          <h2>4. Data Sharing and Disclosure</h2>
          <p>
            We do <strong>not</strong> sell, rent, or trade your personal
            information to third parties. We may share data only in the
            following limited circumstances:
          </p>
          <ul>
            <li>
              <strong>Service Providers:</strong> With trusted third-party
              vendors who assist in operating the App (for example, cloud
              hosting, analytics, error monitoring). These providers are
              contractually obligated to protect your data and use it only for
              the services they provide to us.
            </li>
            <li>
              <strong>Shopify:</strong> As required by the Shopify platform
              for App installation, authentication, and API access.
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law, court
              order, subpoena, or government regulation, or to protect our
              rights, safety, or property.
            </li>
            <li>
              <strong>Business Transfers:</strong> In the event of a merger,
              acquisition, or sale of assets, your data may be transferred as
              part of the transaction. We will provide notice before your data
              is subject to a different privacy policy.
            </li>
          </ul>
        </section>

        <section>
          <h2>5. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active
            or as needed to provide the App services. When you uninstall the
            App, we will delete or anonymize your data within a reasonable
            timeframe, unless retention is required for:
          </p>
          <ul>
            <li>Compliance with legal or regulatory obligations.</li>
            <li>Resolution of disputes or enforcement of agreements.</li>
            <li>Legitimate business purposes such as fraud prevention.</li>
          </ul>
          <p>
            Aggregated, de-identified data that cannot be used to identify you
            may be retained indefinitely for analytics and service improvement
            purposes.
          </p>
        </section>

        <section>
          <h2>6. Data Security</h2>
          <p>
            We take the security of your data seriously and implement
            reasonable technical and organizational measures to protect it,
            including:
          </p>
          <ul>
            <li>
              Encryption of data in transit using TLS/SSL certificates.
            </li>
            <li>
              Secure authentication through Shopify&apos;s OAuth protocol and
              session tokens.
            </li>
            <li>
              Access controls limiting data access to authorized personnel
              only.
            </li>
            <li>
              Regular security reviews and monitoring of our infrastructure.
            </li>
          </ul>
          <p>
            While we strive to protect your information, no method of
            electronic storage or transmission is completely secure. We cannot
            guarantee absolute security but are committed to promptly
            addressing any security incidents.
          </p>
        </section>

        <section>
          <h2>7. Cookies and Tracking Technologies</h2>
          <p>
            The App may use cookies and similar technologies (such as local
            storage and session storage) to maintain authentication state,
            remember preferences, and collect usage analytics. These are
            essential for App functionality and are not used for advertising
            or cross-site tracking.
          </p>
        </section>

        <section>
          <h2>8. Third-Party Links</h2>
          <p>
            The App may contain links to third-party websites or services
            that are not operated by us. We are not responsible for the
            privacy practices of these third parties. We encourage you to
            review their privacy policies before providing any personal
            information.
          </p>
        </section>

        <section>
          <h2>9. Your Rights</h2>
          <p>
            Depending on your jurisdiction, you may have the following rights
            regarding your personal data:
          </p>
          <ul>
            <li>
              <strong>Access:</strong> Request a copy of the personal data we
              hold about you.
            </li>
            <li>
              <strong>Correction:</strong> Request correction of inaccurate or
              incomplete data.
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of your personal
              data, subject to legal retention requirements.
            </li>
            <li>
              <strong>Portability:</strong> Request your data in a structured,
              machine-readable format where technically feasible.
            </li>
            <li>
              <strong>Objection:</strong> Object to certain processing
              activities where applicable under local law.
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the
            details provided below. We will respond to your request within a
            reasonable timeframe and in accordance with applicable law.
          </p>
        </section>

        <section>
          <h2>10. Children&apos;s Privacy</h2>
          <p>
            The App is designed for use by Shopify merchants and is not
            directed at individuals under the age of 16. We do not knowingly
            collect personal information from children. If we become aware
            that we have collected data from a child, we will take steps to
            delete it promptly.
          </p>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <p>
            Your data may be processed and stored in servers located outside
            your country of residence. By using the App, you consent to the
            transfer of your information to countries that may have different
            data protection regulations. We take appropriate safeguards to
            ensure your data remains protected in accordance with this
            Privacy Policy.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We reserve the right to update this Privacy Policy at any time.
            When we make material changes, we will update the &quot;Effective
            date&quot; at the top of this page. We encourage you to review
            this page periodically to stay informed about how we protect your
            information. Continued use of the App after changes are posted
            constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className={styles.contact}>
          <h2>13. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this
            Privacy Policy or our data practices, please contact us:
          </p>
          <div className={styles.contactCard}>
            <p>
              <strong>Touche</strong>
            </p>
            <p>
              Email:{" "}
              <a href="mailto:support@touche.live">support@touche.live</a>
            </p>
          </div>
        </section>
      </article>
    </main>
  );
};

const PrivacyPolicyPage = PrivacyPolicyPageComponent;
export { PrivacyPolicyPage };
